import { CSSProperties, FC, ReactNode, ReactElement, JSXElementConstructor, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { captureHandledException } from "src/services/sentry";

import Button from "../Button";

export const MESSAGE_TYPES = {
  ERROR: "error",
  WARNING: "warning",
  SUCCESS: "success",
} as const;

export type MessageType = (typeof MESSAGE_TYPES)[keyof typeof MESSAGE_TYPES];

interface ValidationResult {
  isValid: boolean;
  message?: string;
  messageType?: MessageType;
}

export const showError = (message: string): ValidationResult => ({
  isValid: false,
  message,
  messageType: MESSAGE_TYPES.ERROR,
});

export const showWarning = (message: string): ValidationResult => ({
  isValid: false,
  message,
  messageType: MESSAGE_TYPES.WARNING,
});

export const showSuccess = (message: string): ValidationResult => ({
  isValid: false,
  message,
  messageType: MESSAGE_TYPES.SUCCESS,
});

export const validationPassed = (): ValidationResult => ({
  isValid: true,
});

interface ValidatedActionButtonProps {
  onAction: () => Promise<void> | void;
  validate: () => ValidationResult;
  children: ReactNode;
  variant?: "contained" | "outlined" | "text";
  color?: "primary" | "secondary" | "error" | "warning" | "info" | "success";
  disabled?: boolean;
  icon?: ReactElement<any, string | JSXElementConstructor<any>>;
  title?: string;
  messageStyle?: CSSProperties;
  messageClassName?: string;
  messagePosition?: "below" | "right";
  renderMessage?: (message: string, type: MessageType) => ReactNode;
  indicateLoading?: boolean;
}

const ValidatedActionButton: FC<ValidatedActionButtonProps> = ({
  onAction,
  validate,
  children,
  variant = "contained",
  color = "primary",
  disabled = false,
  icon,
  title,
  messageStyle = {},
  messageClassName = "",
  messagePosition = "right",
  renderMessage,
  indicateLoading = false,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<MessageType>(MESSAGE_TYPES.ERROR);

  const handleClick = useCallback(async () => {
    try {
      // Clear previous message
      setMessage(null);

      // Run validation
      const validationResult = validate();

      if (!validationResult.isValid) {
        setMessage(validationResult.message || t("validated_action_button.default_validation_error"));
        setMessageType(validationResult.messageType || MESSAGE_TYPES.ERROR);
        return;
      }

      // If validation passes, execute the action
      if (indicateLoading) {
        setIsLoading(true);
      }
      await onAction();
    } catch (error) {
      captureHandledException(error, { component: "ValidatedActionButton", action: "onAction" });
      setMessage(t("validated_action_button.action_failed_error"));
      setMessageType(MESSAGE_TYPES.ERROR);
    } finally {
      if (indicateLoading) {
        setIsLoading(false);
      }
    }
  }, [onAction, validate, t]);

  const getMessageColor = (type: MessageType): string => {
    switch (type) {
      case MESSAGE_TYPES.ERROR:
        return "#d32f2f";
      case MESSAGE_TYPES.WARNING:
        return "#ed6c02";
      case MESSAGE_TYPES.SUCCESS:
        return "#2e7d32";
      default:
        return "#d32f2f";
    }
  };

  const defaultMessageStyles: CSSProperties = {
    color: getMessageColor(messageType),
    fontSize: "0.875rem",
    marginTop: messagePosition === "below" ? "8px" : "0",
    marginLeft: messagePosition === "right" ? "12px" : "0",
    fontWeight: messageType === MESSAGE_TYPES.ERROR ? 500 : 400,
    ...messageStyle,
  };

  const containerStyle: CSSProperties = {
    display: "flex",
    flexDirection: messagePosition === "below" ? "column" : "row",
    alignItems: messagePosition === "below" ? "flex-start" : "center",
  };

  return (
    <div style={containerStyle}>
      <Button
        variant={variant}
        color={color}
        disabled={disabled || (indicateLoading && isLoading)}
        icon={icon}
        title={title}
        onClick={handleClick}
      >
        {indicateLoading && isLoading ? t("validated_action_button.processing") : children}
      </Button>

      {message && (
        <div style={defaultMessageStyles} className={messageClassName}>
          {renderMessage ? renderMessage(message, messageType) : message}
        </div>
      )}
    </div>
  );
};

export default ValidatedActionButton;
